<script setup>
import SlideOver from '@/components/SlideOver.vue';
import { useForm } from '@inertiajs/vue3';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import BadgePill from '@/components/BadgePill.vue';
import { IconCalendarOff } from '@tabler/icons-vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import AppButton from '@/components/AppButton.vue';
import { onMounted, ref, watch } from 'vue';
import axios from 'axios';

const props = defineProps({
    turn: {
        type: Object,
        required: false,
    },
    options: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['close']);
const close = () => {
    emit('close');
};

const form = useForm({
    student_id: props.turn.student_id,
    requester_id: props.turn.requester_id,
    channel: props.turn.channel,
});

const students = ref([]);
const relatives = ref([]);
const fetchStudents = () => {
    axios.get(props.turn.links.fetch_students).then((response) => {
        students.value = response.data.items;
        form.student_id = null;
    });
};

const fetchRelatives = (studentId) => {
    axios.get(route('select.student_relatives.show', { student: studentId })).then((response) => {
        form.requester_id = null;
        relatives.value = response.data.items;
    });
};

watch(
    () => form.student_id,
    (studentId) => {
        fetchRelatives(studentId);
    },
);

onMounted(() => {
    if (props.turn.status === 'available') {
        fetchStudents();
    }
});

const formBlock = useForm({});

const formUnblock = useForm({});

const formRemoveReservation = useForm({});

const formDelete = useForm({});

function submit() {
    form.patch(props.turn.links.assign, {
        preserveScroll: true,
        onSuccess: () => {
            close();
        },
    });
}

function block() {
    formBlock.patch(props.turn.links.block ?? '', {
        preserveScroll: true,
        onSuccess: () => {
            close();
        },
    });
}

function unblock() {
    formUnblock.patch(props.turn.links.unblock ?? '', {
        preserveScroll: true,
        onSuccess: () => {
            close();
        },
    });
}

function removeReservation() {
    formRemoveReservation.patch(props.turn.links.remove_reservation ?? '', {
        preserveScroll: true,
        onSuccess: () => {
            close();
        },
    });
}

function deleteMethod() {
    formDelete.delete(props.turn.links.delete ?? '', {
        preserveScroll: true,
        onSuccess: () => {
            close();
        },
    });
}
</script>

<template>
    <SlideOver :show="props.turn !== null" @close="close">
        <div class="flex justify-between">
            <span class="truncate font-medium">
                {{ turn.group.name }}
            </span>
            <time :datetime="turn.datetime" class="text-gray-500"> {{ turn.time_start }} - {{ turn.time_end }} </time>
        </div>

        <div v-if="turn.status === 'blocked'">
            <div class="flex flex-col items-center">
                <div>
                    <IconCalendarOff class="text-gray-400 w-10 h-10" />
                </div>
                <div>
                    <BadgePill color="gray">
                        {{ $t('messages.not_available') }}
                    </BadgePill>
                </div>
            </div>
        </div>
        <div v-else-if="turn.status === 'reserved'">
            <div>
                <BadgePill color="blue">
                    {{ $t('messages.meeting_turn_reserved') }}
                </BadgePill>
            </div>
            <div>
                <div class="mt-1">{{ $t('messages.requested_by') }}: {{ turn.requester_name }}</div>
                <div class="mt-1">{{ $t('messages.student') }}: {{ turn.student_name }}</div>
                <div class="mt-1">{{ $tc('messages.channels', 1) }}: {{ turn.channel_display }}</div>
                <div v-if="turn.comment" class="mt-1">
                    <div class="mt-1">{{ $tc('messages.comments', 1) }}:</div>
                    <AppWell>
                        <AppCard>
                            <span class="p-1">{{ turn.comment }}</span>
                        </AppCard>
                    </AppWell>
                </div>
            </div>
        </div>
        <div v-else-if="turn.status === 'available'">
            <form method="post" @submit.prevent="submit">
                <BadgePill color="green">
                    {{ $t('messages.meeting_turn_available') }}
                </BadgePill>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.student')" for="student_id" />
                            <FormSelect
                                id="student_id"
                                v-model="form.student_id"
                                :options="students"
                                required
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.student_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="col-span-12" v-auto-animate>
                        <div class="mt-1" v-if="form.student_id !== null">
                            <FormLabel :value="$tc('messages.relatives', 1)" for="requester_id">
                                ({{ relatives.length }})
                            </FormLabel>
                            <FormSelect
                                id="requester_id"
                                v-model="form.requester_id"
                                :options="relatives"
                                :required="relatives.length"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.requester_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.channels', 1)" for="status" />
                            <FormSelect
                                required
                                id="channels"
                                v-model="form.channel"
                                :options="options.channels"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.channel" class="mt-2" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-end mt-3">
                    <SubmitFormButton :button-text="$t('messages.save_meeting')" :processing="form.processing" />
                </div>
            </form>
        </div>

        <div class="flex justify-end mt-3 gap-2">
            <form action="" @submit.prevent="block">
                <AppButton element="button" type="submit" v-if="turn.links.block">
                    {{ $t('messages.block_turn') }}
                </AppButton>
            </form>
            <form action="" @submit.prevent="unblock">
                <AppButton element="button" type="submit" v-if="turn.links.unblock">
                    {{ $t('messages.unblock_turn') }}
                </AppButton>
            </form>
            <form action="" @submit.prevent="removeReservation">
                <AppButton element="button" type="submit" v-if="turn.links.remove_reservation">
                    {{ $t('messages.remove_reservation_turn') }}
                </AppButton>
            </form>
            <form action="" @submit.prevent="deleteMethod">
                <AppButton element="button" type="submit" v-if="turn.links.delete">
                    {{ $t('messages.delete') }}
                </AppButton>
            </form>
        </div>
    </SlideOver>
</template>
